import React, { useEffect, useState } from 'react'
import {
    Box,
    Button,
    CircularProgress,
    Paper,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Typography
} from '@mui/material'

import { FullNodeProp, Patient } from '../../../utils/types'
import { get, post, put } from '../../../utils/http'

interface Props {
    slideData: FullNodeProp
    closeShareModal: Function
}

const PatientsList = ({ slideData, closeShareModal  }: Props) => {
    const [patients, setPatients] =  useState<{[x: string]: Patient[]}>({})
    const [loading, setLoading] = useState<boolean>(false)
    const [listLoading, setListLoading] = useState<boolean>(false)
    const [patientData, setPatientData] =  useState<Patient | null>(null)
    const [imageId, setImageId] = useState<number>(0)
    const [page, setPage] = useState<number>(0)
    const [total, setTotal] = useState<number>(0)

    const rowCount = 100

    const getPatients = async () => {
        setListLoading(true)
        try {
            if (page in patients) return
            let imgId = imageId
            if (imgId === 0) {
                const image = await get(`Images/string-id/${slideData.file_id}`)
                imgId = image.id
                setImageId(image.id)
            }
            const res = await post(`Patients/entity`, { entity_id: 0, page: page, rows: rowCount })
            const temp = res.patients.map((i: Patient) => ({...i, check: i.slides.includes(imgId)}))
            setPatients(prevState => ({...prevState, [page]: temp}))
            setTotal(res.total)
        } catch (err) {
            console.log(err)
        } finally {
            setListLoading(false)
        }
    }

    useEffect(() => {
        getPatients()
    }, [slideData, page])   // eslint-disable-line

    const handleChangePage = (event: any, newPage: any) => setPage(newPage)

    const selectPatient = (data: Patient) => setPatientData(data)

    const addToPatient = async () => {
        setLoading(true)
        try {
            const slides: number[] = patientData!.slides
            if (!slides.includes(imageId)) {
                slides.push(imageId)
                await put(`Patients/${patientData!.id}`, { slides: slides })
            }
            // check previous attached patient
            let prevPatient = null
            for (let i in patients) {
                const tempIndex = patients[i].findIndex(p => p.slides.includes(imageId) && p.id !== patientData!.id)
                if (tempIndex !== -1) {
                    prevPatient = patients[i][tempIndex]
                    break
                }
            }
            if (prevPatient) {
                const fixedSlides = prevPatient.slides.filter(i => i !== imageId)
                await put(`Patients/${prevPatient.id}`, { slides: fixedSlides })
            }
        } catch (err) {
            console.log(err)
        } finally {
            setLoading(false)
            closeShareModal()
        }
    }

    return (
        <Box className='d-flex flex-column gap-4'>
            <Typography variant="h5" component="h1">Patients</Typography>
            <Box className='d-flex flex-column gap-2'>
                <TablePagination
                    component='div'
                    count={total}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowCount}
                    onRowsPerPageChange={() => {}}
                    rowsPerPageOptions={[rowCount]}
                />
                <TableContainer component={Paper}>
                    <Table size="small" aria-label="user table">
                        <TableHead>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell align="right">Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {listLoading
                                ? Array.from({ length: 3 }, (_, i) => i + 1).map(i => (
                                    <TableRow key={i}>
                                        <TableCell><Skeleton height={20} variant='rectangular'/></TableCell>
                                        <TableCell><Skeleton height={20} variant='rectangular'/></TableCell>
                                        <TableCell><Skeleton height={20} variant='rectangular'/></TableCell>
                                    </TableRow>
                                ))
                                : patients[page]?.map(u => (
                                    <TableRow key={u.id} onClick={() => selectPatient(u)} selected={patientData ? patientData.id===u.id : u.check}>
                                        <TableCell component="th" scope="row">{u.patient_id}</TableCell>
                                        <TableCell align="right">{u.status}</TableCell>
                                        <TableCell>
                                            <input
                                                className='form-check-input'
                                                type='checkbox'
                                                checked={patientData ? patientData.id===u.id : u.check}
                                                readOnly
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component='div'
                    count={total}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowCount}
                    onRowsPerPageChange={() => {}}
                    rowsPerPageOptions={[rowCount]}
                />
            </Box>
            <Box className='d-flex gap-3 justify-content-end'>
                {loading
                    ? <CircularProgress size={30} />
                    : <>
                        <Button variant="outlined" onClick={() => closeShareModal()}>Close</Button>
                        <Button disabled={!patientData} variant="contained" onClick={addToPatient}>Submit</Button>
                    </>
                }
            </Box>
        </Box>
    )
}

export default PatientsList